import React from 'react';
import { CheckCircle, RefreshCw, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { ProviderConnection } from '../api/client';

interface SettingsViewProps {
  localMediaHealth: string | null;
  hasLocalMediaConnected: boolean;
  localMediaSaveStatus: string | null;
  localMediaUrl: string;
  setLocalMediaUrl: (url: string) => void;
  localMediaToken: string;
  setLocalMediaToken: (token: string) => void;
  checkLocalMedia: () => void;
  handleSaveLocalMedia: () => void;
  isSavingLocalMedia: boolean;
  providerSaveStatus: string | null;
  handleSaveProviderKey: (e: React.FormEvent) => void;
  providerType: string;
  handleProviderTypeChange: (val: string) => void;
  providerAlias: string;
  setProviderAlias: (alias: string) => void;
  providerBaseUrl: string;
  setProviderBaseUrl: (url: string) => void;
  providerSecretKey: string;
  setProviderSecretKey: (key: string) => void;
  connections: ProviderConnection[];
  handleDeleteConnection: (id: string, alias: string) => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  localMediaHealth,
  hasLocalMediaConnected,
  localMediaSaveStatus,
  localMediaUrl,
  setLocalMediaUrl,
  localMediaToken,
  setLocalMediaToken,
  checkLocalMedia,
  handleSaveLocalMedia,
  isSavingLocalMedia,
  providerSaveStatus,
  handleSaveProviderKey,
  providerType,
  handleProviderTypeChange,
  providerAlias,
  setProviderAlias,
  providerBaseUrl,
  setProviderBaseUrl,
  providerSecretKey,
  setProviderSecretKey,
  connections,
  handleDeleteConnection,
}) => {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">ตั้งค่าการเชื่อมต่อ</h1>
        <p className="text-sm text-slate-400">
          จัดการกุญแจ AI Provider ที่เข้ารหัส AES-256-GCM และตรวจสอบสถานะ Local Media Service
        </p>
      </div>

      {/* Local Media Service Status */}
      <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">Local Media Service</h3>
            <p className="text-xs text-slate-400">เครื่องคอมพิวเตอร์ผู้ใช้ที่เชื่อมต่อผ่าน Cloudflare Tunnel หรือ Localhost</p>
          </div>
          {localMediaHealth && (
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                hasLocalMediaConnected && localMediaHealth.includes('Online')
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : localMediaHealth.includes('Online') && !hasLocalMediaConnected
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  : localMediaHealth.includes('Token')
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
              }`}
            >
              {hasLocalMediaConnected && localMediaHealth.includes('Online')
                ? localMediaHealth
                : localMediaHealth.includes('Online')
                ? 'ทดสอบผ่าน (ยังไม่ได้บันทึก)'
                : localMediaHealth}
            </span>
          )}
        </div>

        {localMediaSaveStatus && (
          <div className="p-3 bg-emerald-950/40 border border-emerald-500/40 rounded-lg text-xs text-emerald-300 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{localMediaSaveStatus}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Service URL (Tunnel หรือ Localhost)</label>
            <input
              type="text"
              value={localMediaUrl}
              onChange={(e) => setLocalMediaUrl(e.target.value)}
              placeholder="https://media-local.g-forge.site"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Bearer API Token (LOCAL_MEDIA_TOKEN)</label>
            <input
              type="password"
              value={localMediaToken}
              onChange={(e) => setLocalMediaToken(e.target.value)}
              placeholder="ใส่ Bearer Token เพื่อยืนยันสิทธิ์"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => checkLocalMedia()}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs font-medium text-sky-400 transition flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            ทดสอบการเชื่อมต่อ
          </button>
          <button
            type="button"
            onClick={handleSaveLocalMedia}
            disabled={isSavingLocalMedia}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 shadow"
          >
            <Save className="w-3.5 h-3.5" />
            {isSavingLocalMedia ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
          </button>
        </div>
      </div>

      {/* Add / Update AI Provider Form */}
      <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">บันทึกหรืออัปเดต API Key ผู้ให้บริการ</h3>
            <p className="text-xs text-slate-400">
              กุญแจทั้งหมดจะถูกเข้ารหัสผ่านฮาร์ดแวร์ด้วย AES-256-GCM บน Backend ทันที และแสดงผลแบบ Masked เท่านั้น
            </p>
          </div>
        </div>

        {providerSaveStatus && (
          <div className="p-3 bg-emerald-950/40 border border-emerald-500/40 rounded-lg text-xs text-emerald-300 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            {providerSaveStatus}
          </div>
        )}

        <form onSubmit={handleSaveProviderKey} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">เลือกผู้ให้บริการ</label>
              <select
                value={providerType}
                onChange={(e) => handleProviderTypeChange(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
              >
                <option value="gemini">Google Gemini</option>
                <option value="flux">Together AI FLUX</option>
                <option value="kling">Kling AI</option>
                <option value="custom">Custom Adapter</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">ชื่อเรียก</label>
              <input
                type="text"
                required
                value={providerAlias}
                onChange={(e) => setProviderAlias(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Base API URL</label>
              <input
                type="text"
                required
                value={providerBaseUrl}
                onChange={(e) => setProviderBaseUrl(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                API Secret Key <span className="text-amber-400">*</span>
              </label>
              <input
                type="password"
                required
                value={providerSecretKey}
                onChange={(e) => setProviderSecretKey(e.target.value)}
                placeholder="ใส่ API Key เช่น AIzaSy... หรือ sk-..."
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500 font-mono"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-5 py-2 bg-sky-500 hover:bg-sky-600 rounded-lg text-xs font-semibold text-white transition shadow-lg shadow-sky-500/20 flex items-center gap-1.5"
            >
              <ShieldCheck className="w-4 h-4" /> บันทึกและเข้ารหัสกุญแจ
            </button>
          </div>
        </form>
      </div>

      {/* Encrypted AI Providers List */}
      <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
        <h3 className="font-semibold text-white">ผู้ให้บริการที่ลงทะเบียนแล้ว</h3>
        <div className="space-y-3">
          {connections.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-800 rounded-xl bg-slate-900/40 text-slate-400 text-xs">
              ยังไม่มีการเชื่อมต่อ AI Provider กรุณาเพิ่มกุญแจด้านบน
            </div>
          ) : (
            connections.map((prov) => (
              <div key={prov.id} className="flex items-center justify-between p-3.5 bg-slate-900/60 rounded-xl border border-slate-800">
                <div>
                  <div className="text-sm font-medium text-slate-200">{prov.alias}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Adapter: <span className="text-slate-400 font-mono">{prov.adapterCode}</span> &bull; สถานะ: เข้ารหัสระดับฮาร์ดแวร์ AES-256-GCM
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono bg-slate-800 px-2.5 py-1 rounded text-emerald-400 border border-slate-700">
                    {prov.maskedIdentity || '****'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteConnection(prov.id, prov.alias)}
                    title="ลบการเชื่อมต่อนี้"
                    className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
